class ScoreggiaRandomBackground extends HTMLElement {
  constructor() {
    super();
    this.mediaQuery = window.matchMedia('(max-width: 749px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    this.posterMount = this.querySelector('[data-background-poster]');
    this.videoMount = this.querySelector('[data-background-video]');
    this.mediaQuery.addEventListener('change', this.handleViewportChange);
    this.reducedMotionQuery.addEventListener('change', this.handleViewportChange);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.refresh();
  }

  disconnectedCallback() {
    this.mediaQuery.removeEventListener('change', this.handleViewportChange);
    this.reducedMotionQuery.removeEventListener('change', this.handleViewportChange);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.destroyVideo();
  }

  get records() {
    return Array.from(this.querySelectorAll('[data-scoreggia-background-slot]'));
  }

  get eligibleRecords() {
    return this.records.filter(
      (record) => record.dataset.enabled === 'true' && record.dataset.hasMedia === 'true',
    );
  }

  randomValue() {
    if (window.crypto?.getRandomValues) {
      const value = new Uint32Array(1);
      window.crypto.getRandomValues(value);
      return value[0] / 4294967296;
    }

    return Math.random();
  }

  readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  writeSession(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (_error) {
      // The background remains functional when storage is unavailable.
    }
  }

  weightedChoice(records) {
    const totalWeight = records.reduce((sum, record) => sum + Math.max(Number(record.dataset.weight) || 1, 1), 0);
    let position = this.randomValue() * totalWeight;

    for (const record of records) {
      position -= Math.max(Number(record.dataset.weight) || 1, 1);
      if (position < 0) return record;
    }

    return records[records.length - 1];
  }

  chooseRecord(forcedSlot) {
    const records = this.eligibleRecords;
    if (records.length === 0) return null;

    if (forcedSlot) {
      return records.find((record) => record.dataset.scoreggiaBackgroundSlot === forcedSlot) || null;
    }

    if (this.dataset.selectionMode === 'fixed') {
      const fixedRecord = records.find((record) => record.dataset.scoreggiaBackgroundSlot === this.dataset.fixedSlot);
      return fixedRecord || this.weightedChoice(records);
    }

    if (this.dataset.selectionMode === 'random_session') {
      const storedSlot = this.readSession('scoreggia-background-session-slot');
      const storedRecord = records.find((record) => record.dataset.scoreggiaBackgroundSlot === storedSlot);
      if (storedRecord) return storedRecord;

      const sessionRecord = this.weightedChoice(records);
      this.writeSession('scoreggia-background-session-slot', sessionRecord.dataset.scoreggiaBackgroundSlot);
      return sessionRecord;
    }

    let candidates = records;
    const previousSlot = this.readSession('scoreggia-background-last-slot');
    if (this.dataset.avoidRepeat === 'true' && records.length > 1 && previousSlot) {
      candidates = records.filter((record) => record.dataset.scoreggiaBackgroundSlot !== previousSlot);
    }

    const pageRecord = this.weightedChoice(candidates);
    this.writeSession('scoreggia-background-last-slot', pageRecord.dataset.scoreggiaBackgroundSlot);
    return pageRecord;
  }

  refresh(forcedSlot) {
    if (this.dataset.enabled !== 'true') {
      this.activeRecord = null;
      this.clearMedia();
      this.dataset.eligibleCount = String(this.eligibleRecords.length);
      this.dataset.backgroundState = 'disabled';
      return;
    }

    this.activeRecord = this.chooseRecord(forcedSlot);
    this.renderActiveRecord();
  }

  handleViewportChange() {
    this.renderActiveRecord();
  }

  handleVisibilityChange() {
    if (this.dataset.pauseWhenHidden !== 'true' || !this.activeVideo) return;

    if (document.hidden) {
      this.activeVideo.pause();
      this.dataset.videoState = 'paused-hidden';
    } else {
      const video = this.activeVideo;
      video
        .play()
        .then(() => {
          if (video === this.activeVideo) this.dataset.videoState = 'playing';
        })
        .catch(() => {
          if (video !== this.activeVideo) return;
          this.classList.remove('scoreggia-background--video-ready');
          this.destroyVideo();
          this.dataset.videoState = 'rejected';
        });
    }
  }

  templateFor(record, kind) {
    const mobile = this.mediaQuery.matches;
    if (mobile) {
      return (
        record.querySelector(`template[data-background-${kind}-mobile]`) ||
        record.querySelector(`template[data-background-${kind}-desktop]`)
      );
    }

    return record.querySelector(`template[data-background-${kind}-desktop]`);
  }

  renderActiveRecord() {
    this.clearMedia();
    const record = this.activeRecord;
    this.dataset.eligibleCount = String(this.eligibleRecords.length);
    if (!record) {
      this.dataset.backgroundState = 'fallback';
      return;
    }

    const slot = record.dataset.scoreggiaBackgroundSlot;
    this.dataset.activeSlot = slot;
    this.dataset.backgroundState = 'selected';
    this.dataset.selectedDevice = this.mediaQuery.matches ? 'mobile' : 'desktop';
    this.style.setProperty('--scoreggia-background-position', record.dataset.safePosition || 'center center');

    const reducedMotion = this.reducedMotionQuery.matches;
    const dataSaving = Boolean(navigator.connection?.saveData);
    const allowReducedPoster = this.dataset.posterReducedMotion === 'true';
    const allowDataPoster = this.dataset.posterDataSaving === 'true';
    const shouldShowPoster = (!reducedMotion || allowReducedPoster) && (!dataSaving || allowDataPoster);

    if (shouldShowPoster) {
      this.renderPoster(record);
    } else {
      this.dataset.posterState = 'suppressed';
    }
    if (reducedMotion || dataSaving) {
      this.dataset.playbackPolicy = reducedMotion ? 'reduced-motion' : 'data-saving';
      this.dataset.videoState = 'suppressed';
      return;
    }

    this.dataset.playbackPolicy = 'standard';
    window.requestAnimationFrame(() => this.renderVideo(record));
  }

  renderPoster(record) {
    const template = this.templateFor(record, 'poster');
    if (!template || !this.posterMount) {
      this.dataset.posterState = 'missing';
      return;
    }

    this.posterMount.replaceChildren(template.content.cloneNode(true));
    this.classList.add('scoreggia-background--has-poster');
    this.dataset.posterState = 'mounted';
  }

  renderVideo(record) {
    if (!this.isConnected || record !== this.activeRecord || !this.videoMount) return;

    const template = this.templateFor(record, 'video');
    if (!template) {
      this.dataset.videoState = 'missing';
      return;
    }

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (!video) {
      this.dataset.videoState = 'invalid';
      return;
    }

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('aria-hidden', 'true');

    video.addEventListener(
      'playing',
      () => {
        if (video !== this.activeVideo) return;
        this.classList.add('scoreggia-background--video-ready');
        this.dataset.videoState = 'playing';
      },
      { once: true },
    );

    video.addEventListener(
      'error',
      () => {
        if (video !== this.activeVideo) return;
        this.classList.remove('scoreggia-background--video-ready');
        this.destroyVideo();
        this.dataset.videoState = 'error';
      },
      { once: true },
    );

    this.videoMount.replaceChildren(fragment);
    this.activeVideo = video;
    this.dataset.videoState = 'loading';
    const playback = video.play();
    if (playback) {
      playback.catch(() => {
        if (video !== this.activeVideo) return;
        this.classList.remove('scoreggia-background--video-ready');
        this.destroyVideo();
        this.dataset.videoState = 'rejected';
      });
    }
  }

  destroyVideo() {
    if (this.activeVideo) {
      this.activeVideo.pause();
      this.activeVideo.removeAttribute('src');
      this.activeVideo.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      this.activeVideo.load();
    }
    this.activeVideo = null;
    this.videoMount?.replaceChildren();
  }

  clearMedia() {
    this.classList.remove('scoreggia-background--has-poster', 'scoreggia-background--video-ready');
    this.removeAttribute('data-active-slot');
    this.removeAttribute('data-background-state');
    this.removeAttribute('data-selected-device');
    this.removeAttribute('data-playback-policy');
    this.removeAttribute('data-poster-state');
    this.removeAttribute('data-video-state');
    this.destroyVideo();
    this.posterMount?.replaceChildren();
  }
}

if (!customElements.get('scoreggia-random-background')) {
  customElements.define('scoreggia-random-background', ScoreggiaRandomBackground);
}

if (!window.scoreggiaBackgroundEditorEventsBound) {
  window.scoreggiaBackgroundEditorEventsBound = true;

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelector('scoreggia-random-background')?.refresh();
  });

  document.addEventListener('shopify:section:select', (event) => {
    const background = event.target.matches('scoreggia-random-background')
      ? event.target
      : event.target.querySelector('scoreggia-random-background');
    background?.refresh();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const record = event.target.closest('[data-scoreggia-background-slot]');
    const background = event.target.closest('scoreggia-random-background');
    if (record && background) background.refresh(record.dataset.scoreggiaBackgroundSlot);
  });
}
